import React, { useContext } from "react";
import IOrder from "../../../models/IOrder";
import OwLabel from "../../shared/OwLabel/OwLabel";
import UserService from "../../../services/UserService";
import { isDeliveryDeadlinesEnabled } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import { useNavigate } from "react-router-dom";
import * as _ from "lodash-es";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import VendorContext from "../../../contexts/VendorContext";
import UiContext from "../../../contexts/UiContext";
import HelperService from "../../../services/HelperService";
import CartService from "../../../services/CartService";
import { KitItemsDisplay } from "../../shared/KitItemsDisplay/KitItemsDisplay";
import { ReorderConfirmModal } from "../ReorderConfirmModal/ReorderConfirmModal";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import OwButton from "../../shared/OwButton/OwButton";
import "./EcommerceOrderDetailRow.scss";

type Props = {
    order: IOrder;
};

export const EcommerceOrderDetailRow: React.FC<Props> = (props) => {
    const navigate = useNavigate();
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    const viewOrderDetails = () => {
        // View Details
        navigate(`/ecomm/orders/details/${props.order.guid}`);
    };

    const viewInvoice = () => {
        // View Invoice
        navigate(`/ecomm/orders/invoice/${props.order.guid}`);
    };

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const executeReorder = async (clearExistingItems: boolean) => {
        closeModal();
        uiContext.setActivityIndicator(true);

        try {
            const result = await CartService.createCartFromOrder(
                {
                    orderGuid: props.order.guid,
                    clearExistingItems,
                    skipUnavailableItems: true,
                },
                userModel,
                supplierModel,
                userModel.isSupplierUser ? undefined : vendorModel
            );

            // Update cart item count (exclude kit child items)
            const count = result.cart.Cart_Items?.filter((i) => !i.parent_cart_item_id).length || 0;
            uiContext.setCartItemCount(count);

            // Show success toast
            uiContext.notifyFn(null, {
                message: "reorder_success",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });

            navigate("/ecomm");
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "reorder_failed";
            uiContext.notifyFn({
                eid: "",
                title: "error_reorder",
                message: errorMessage,
            });
        } finally {
            uiContext.setActivityIndicator(false);
        }
    };

    const showReorderConfirmModal = () => {
        const modalComponent = (
            <ReorderConfirmModal
                closeModalFn={closeModal}
                keepExistingFn={() => executeReorder(false)}
                clearCartFn={() => executeReorder(true)}
            />
        );

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const reOrder = () => {
        if (uiContext.cartItemCount > 0) {
            showReorderConfirmModal();
        } else {
            executeReorder(false);
        }
    };

    const formatMoney = (value?: number | null) =>
        HelperService.formatCurrency({
            value: value || 0,
            currency: "USD",
            precision: supplierModel.pricing_precision || 2,
        });

    const statusName = _.startCase(props.order.Order_State?.name.toLowerCase());
    // Order-level files (not item images); shown only when the supplier exposes order files in eCommerce.
    const fileCount = supplierModel.ecomm_order_file_uploads
        ? (props.order.Attachments || props.order.attachments || []).filter((attachment) => !attachment.image_seq).length
        : 0;

    return (
        <div className={"EcommerceOrderDetailRow"}>
            <div className={"order-row-header"}>
                <div className={"order-row-title"}>
                    <button type={"button"} className={"order-row-name"} onClick={viewOrderDetails}>
                        {props.order.name}
                    </button>
                    {statusName ? (
                        <span className={`order-row-status ${props.order.is_cancelled ? "is-cancelled" : ""}`}>{statusName}</span>
                    ) : null}
                    {supplierModel.Supplier_Integration.has_qbd && props.order.qbd_RefNumber ? (
                        <span className={"order-row-ref"}>QBE SO# {props.order.qbd_RefNumber}</span>
                    ) : null}
                    {fileCount > 0 ? (
                        <button type={"button"} className={"order-row-files"} onClick={viewOrderDetails} data-testid={"order-row-files"}>
                            <i className={"fas fa-paperclip"} aria-hidden={true} />
                            {fileCount} <OwLabel title={fileCount === 1 ? "file" : "files"} noBottomMargin={true} />
                        </button>
                    ) : null}
                </div>

                <div className={"order-row-actions"}>
                    <OwButton
                        title={"invoice"}
                        className={"order-row-btn-secondary"}
                        leftIconClass={"fas fa-file-invoice"}
                        fn={viewInvoice}
                    />
                    <OwButton title={"reorder"} className={"order-row-btn-secondary"} leftIconClass={"fas fa-rotate-right"} fn={reOrder} />
                    <OwButton title={"order_details"} className={"order-row-btn-primary"} fn={viewOrderDetails} />
                </div>

                <dl className={"order-row-meta"}>
                    <div>
                        <dt>
                            <OwLabel title={"order_placed"} noBottomMargin={true} />
                        </dt>
                        <dd>{UserService.parseDate(props.order.submit_date)}</dd>
                    </div>
                    {isDeliveryDeadlinesEnabled(props.order.Vendor) && props.order.delivery_date ? (
                        <div>
                            <dt>
                                <OwLabel title={"delivery_date"} noBottomMargin={true} />
                            </dt>
                            <dd>{UserService.parseUTCDate(props.order.delivery_date)}</dd>
                        </div>
                    ) : null}
                    {!hidePricing ? (
                        <div>
                            <dt>
                                <OwLabel title={"total"} noBottomMargin={true} />
                            </dt>
                            <dd className={"order-row-total"}>{formatMoney(props.order.grand_total)}</dd>
                        </div>
                    ) : null}
                    <div>
                        <dt>
                            <OwLabel title={"ship_to"} noBottomMargin={true} />
                        </dt>
                        <dd>{props.order.Ship_To?.company_name || props.order.Ship_To?.name}</dd>
                    </div>
                    {props.order.po_no ? (
                        <div>
                            <dt>
                                <OwLabel title={"po_no"} noBottomMargin={true} />
                            </dt>
                            <dd>{props.order.po_no}</dd>
                        </div>
                    ) : null}
                    {supplierModel.has_order_due_date ? (
                        <div>
                            <dt>
                                <OwLabel title={"due_date"} noBottomMargin={true} />
                            </dt>
                            <dd>{UserService.parseDate(props.order.due_date)}</dd>
                        </div>
                    ) : null}
                </dl>
            </div>

            <ul className={"order-row-items"}>
                {props.order.Order_Items.map((orderItem) => (
                    <li className={"order-row-item"} key={orderItem.guid}>
                        <div className={"order-row-item-image"}>
                            <OwS3ImageViewer
                                catalogItem={orderItem.Catalog_Item}
                                isMicroView={true}
                                attachmentIndex={0}
                                imageIndex={IImageSizeEnum.THUMB}
                                className={""}
                                includeAllImages={false}
                                hideEmptyImage={false}
                            />
                        </div>
                        <div className={"order-row-item-info"}>
                            <div className={"order-row-item-name"}>{orderItem.Catalog_Item?.name}</div>
                            {orderItem.description || orderItem.Catalog_Item?.description ? (
                                <div className={"order-row-item-description"}>
                                    {orderItem.description || orderItem.Catalog_Item?.description}
                                </div>
                            ) : null}
                            {orderItem.Catalog_Item?.sku ? (
                                <div className={"order-row-item-sku"}>
                                    <OwLabel title={"sku"} noBottomMargin={true} /> {orderItem.Catalog_Item.sku}
                                </div>
                            ) : null}
                            <KitItemsDisplay orderItem={orderItem} />
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};
